import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UploadCloud, Zap, Clock, Eye, Heart, DollarSign, Users, Share2, Bell,
  Trash2, Loader2, CalendarClock, CheckCircle2, LogOut, Rocket } from "lucide-react";
import { toast } from "sonner";
import { api, resolveMedia } from "@/lib/site";
import { useAuth } from "@/lib/auth";
import { Switch } from "@/components/ui/switch";

const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

const StatTile = ({ icon: Icon, label, value, i }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
    className="border border-white/10 bg-[#0b0b0b] p-5" data-testid={`stat-${label.toLowerCase().replace(/ /g,'-')}`}>
    <Icon size={20} className="text-gold mb-3" strokeWidth={1.4} />
    <div className="font-display text-3xl text-white leading-none">{value}</div>
    <div className="text-white/45 text-xs uppercase tracking-widest mt-2">{label}</div>
  </motion.div>
);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [data, setData] = useState(null);
  const [content, setContent] = useState({ scheduled: [], published: [] });
  const [staged, setStaged] = useState([]);      // uploaded-but-not-queued items
  const [time, setTime] = useState("20:00");
  const [interval, setIntervalDays] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [queuing, setQueuing] = useState(false);

  const load = async () => {
    const [d, c] = await Promise.all([api.get("/dashboard"), api.get("/content")]);
    setData(d.data); setContent(c.data);
    setTime(d.data.autopilot.time_of_day); setIntervalDays(d.data.autopilot.interval_days);
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const results = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/content/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        results.push(data);
      }
      setStaged((s) => [...s, ...results]);
      toast.success(`${results.length} file(s) uploaded — ready for AutoPilot`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const queueAll = async () => {
    if (!staged.length) return toast.error("Upload some content first");
    setQueuing(true);
    try {
      await api.post("/content/bulk", {
        items: staged.map((s, i) => ({ type: s.type, title: s.title || `Drop ${i + 1}`, media_url: s.media_url })),
        time_of_day: time, interval_days: Number(interval),
      });
      setStaged([]);
      toast.success("AutoPilot scheduled your content. Sit back — we handle the rest.");
      load();
    } catch { toast.error("Could not schedule"); }
    finally { setQueuing(false); }
  };

  const toggleAutopilot = async (val) => {
    setData((d) => ({ ...d, autopilot: { ...d.autopilot, enabled: val } }));
    await api.post("/autopilot", { enabled: val, time_of_day: time, interval_days: Number(interval) });
    toast.success(val ? "AutoPilot ON" : "AutoPilot paused");
  };

  const remove = async (id) => { await api.delete(`/content/${id}`); load(); };

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>;
  const s = data.stats;

  return (
    <div className="pt-28 pb-24 max-w-7xl mx-auto px-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-6 mb-6">
        <div>
          <p className="overline text-brand mb-2">Creator Dashboard</p>
          <h1 className="font-display text-5xl md:text-6xl uppercase text-white leading-none">
            {data.creator.name || data.creator.handle}
          </h1>
          <p className="text-white/50 mt-2">Your one job is to create. Fan Focus handles the rest.</p>
        </div>
        <button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn"
          className="inline-flex items-center gap-2 border border-white/20 text-white/80 px-5 py-2.5 overline hover:border-gold hover:text-gold transition-colors">
          <LogOut size={15} /> Log out
        </button>
      </div>

      {/* Starter Token chip */}
      {data.creator?.starter_token?.active && (() => {
        const days = Math.max(0, Math.ceil((new Date(data.creator.starter_token.expires_at) - Date.now()) / 86400000));
        return (
          <div className="mb-8 flex items-center gap-3 border border-brand/40 bg-pink-500/10 rounded-xl px-5 py-3" data-testid="starter-chip">
            <span className="text-2xl">🎁</span>
            <div className="flex-1">
              <div className="text-white font-semibold">Founding Creator Starter Token — Seat #{data.creator.starter_token.seat} of 10</div>
              <div className="text-white/60 text-xs">Free access active · {days} days remaining · Auto-billing begins after 90 days</div>
            </div>
          </div>
        );
      })()}

      {/* AutoPilot control bar */}
      <div className="border border-gold/40 bg-gradient-to-br from-[#171203] to-[#0b0b0b] p-6 mb-8 flex flex-wrap items-center justify-between gap-5" data-testid="autopilot-bar">
        <div className="flex items-center gap-4">
          <Zap className={data.autopilot.enabled ? "text-gold" : "text-white/30"} size={30} />
          <div>
            <div className="font-display text-2xl uppercase text-white">AutoPilot Mode</div>
            <div className="text-white/50 text-sm">
              {data.autopilot.enabled ? `Publishing 1 post every ${data.autopilot.interval_days} day(s) at ${data.autopilot.time_of_day} UTC` : "Paused"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="overline text-white/60">{data.autopilot.enabled ? "On" : "Off"}</span>
          <Switch checked={data.autopilot.enabled} onCheckedChange={toggleAutopilot} data-testid="autopilot-toggle" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-12">
        <StatTile icon={CalendarClock} label="Scheduled" value={s.scheduled} i={0} />
        <StatTile icon={CheckCircle2} label="Published" value={s.published} i={1} />
        <StatTile icon={Eye} label="Views" value={s.views.toLocaleString()} i={2} />
        <StatTile icon={Heart} label="Likes" value={s.likes.toLocaleString()} i={3} />
        <StatTile icon={DollarSign} label="Earnings" value={`$${s.earnings.toLocaleString()}`} i={4} />
        <StatTile icon={Users} label="Subscribers" value={s.subscribers.toLocaleString()} i={5} />
        <StatTile icon={Share2} label="Referrals" value={s.referrals} i={6} />
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Upload + schedule */}
        <div>
          <h2 className="font-display text-3xl uppercase text-white mb-4">Upload Content</h2>
          <div onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-white/15 hover:border-gold/50 transition-colors cursor-pointer p-10 text-center bg-[#0b0b0b]"
            data-testid="upload-dropzone">
            <input ref={fileRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={onFiles} data-testid="file-input" />
            {uploading ? <Loader2 className="animate-spin text-gold mx-auto" /> :
              <><UploadCloud className="text-gold mx-auto mb-3" size={34} strokeWidth={1.3} />
              <p className="text-white/80">Drop or select photos & videos</p>
              <p className="text-white/40 text-sm mt-1">Upload 30 at once — AutoPilot drips them out for you</p></>}
          </div>

          {staged.length > 0 && (
            <div className="mt-5 border border-white/10 bg-[#0b0b0b] p-4" data-testid="staged-panel">
              <div className="flex items-center justify-between mb-3">
                <span className="overline text-gold">{staged.length} ready to schedule</span>
              </div>
              <div className="grid grid-cols-6 gap-2 mb-4">
                {staged.slice(0, 12).map((it, i) => (
                  <img key={i} src={resolveMedia(it.media_url)} alt="" className="aspect-square object-cover" />
                ))}
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <label className="text-white/60 text-sm">Post at
                  <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="schedule-time"
                    className="ml-2 bg-black border border-white/15 text-white px-2 py-1.5" />
                </label>
                <label className="text-white/60 text-sm">Every
                  <select value={interval} onChange={(e) => setIntervalDays(e.target.value)} data-testid="schedule-interval"
                    className="ml-2 bg-black border border-white/15 text-white px-2 py-1.5">
                    <option value={1}>1 day</option><option value={2}>2 days</option><option value={3}>3 days</option><option value={7}>week</option>
                  </select>
                </label>
                <button onClick={queueAll} disabled={queuing} data-testid="queue-btn"
                  className="ml-auto inline-flex items-center gap-2 bg-gold text-black px-6 py-2.5 overline hover:bg-[#F5D061] transition-colors disabled:opacity-60">
                  {queuing ? <Loader2 size={15} className="animate-spin" /> : <><Rocket size={15} /> Launch AutoPilot</>}
                </button>
              </div>
            </div>
          )}

          {/* Scheduled queue */}
          <h3 className="font-display text-2xl uppercase text-white mt-10 mb-4 flex items-center gap-2"><Clock size={20} className="text-gold" /> AutoPilot Queue</h3>
          <div className="space-y-2" data-testid="queue-list">
            {content.scheduled.length === 0 && <p className="text-white/40 text-sm">Nothing queued yet.</p>}
            {content.scheduled.map((it) => (
              <div key={it.id} className="flex items-center gap-4 border border-white/10 bg-[#0b0b0b] p-3" data-testid={`queued-${it.id}`}>
                <img src={resolveMedia(it.media_url)} alt="" className="w-14 h-14 object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate">{it.title}</div>
                  <div className="text-gold/80 text-xs uppercase tracking-wider">{fmt(it.scheduled_at)}</div>
                </div>
                <button onClick={() => remove(it.id)} className="text-white/40 hover:text-red-400" data-testid={`del-${it.id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Published feed */}
        <div>
          <h2 className="font-display text-3xl uppercase text-white mb-4 flex items-center gap-2"><Bell size={22} className="text-gold" /> Published Automatically</h2>
          <p className="text-white/50 text-sm mb-4">{s.notifications_sent} subscriber notifications sent automatically.</p>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="published-list">
            {content.published.length === 0 && <p className="text-white/40 text-sm">No posts published yet.</p>}
            {content.published.map((it) => (
              <div key={it.id} className="border border-white/10 bg-[#0b0b0b] overflow-hidden" data-testid={`published-${it.id}`}>
                <img src={resolveMedia(it.media_url)} alt="" className="w-full h-40 object-cover" />
                <div className="p-4">
                  <div className="text-white truncate mb-2">{it.title}</div>
                  <div className="flex items-center justify-between text-xs text-white/50">
                    <span className="flex items-center gap-1"><Eye size={13} /> {it.views.toLocaleString()}</span>
                    <span className="flex items-center gap-1"><Heart size={13} /> {it.likes}</span>
                    <span className="flex items-center gap-1 text-gold"><DollarSign size={13} /> {it.earnings}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
